<script setup lang="ts">
import axios from 'axios'
import { notifyError } from '@/utils/helpers'



const TB_BASE_URL = import.meta.env.VITE_TB_URL
const theme = useTheme()
const isLoading = ref(false)

// ── ThingsBoard auth ──────────────────────────────────────────────────────────

const tbToken = ref<string | null>(null)
const loginError = ref('')

async function getTbToken(): Promise<string | null> {
    if (tbToken.value) return tbToken.value
    try {
        const response = await apiOmni.post('api/tb/login')
        tbToken.value = response.data.token
        return tbToken.value
    } catch (e: unknown) {
        const err = e as { response?: { status: number; data: unknown }; message?: string }
        loginError.value = err.response
            ? `HTTP ${err.response.status}: ${JSON.stringify(err.response.data)}`
            : (err.message ?? String(e))
        notifyError(loginError.value)
        return null
    }
}

function tbHeaders(token: string): Record<string, string> {
    return {
        'X-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    }
}

// ── Types ─────────────────────────────────────────────────────────────────────

declare interface TbDeviceId {
    id: string
    entityType: string
}

declare interface TbDevice {
    id: TbDeviceId
    name: string
    type: string
    label: string
    deviceProfileId: { id: string; entityType: string }
    createdTime: number
    additionalInfo?: Record<string, unknown>
}

declare interface TbPageData<T> {
    data: T[]
    totalPages: number
    totalElements: number
    hasNext: boolean
}

declare interface TreeNode {
    id: string
    title: string
    children?: TreeDevice[]
    isType?: boolean
}

declare interface TreeDevice {
    id: string
    title: string
    label?: string
    deviceId: string
    type: string
    createdTime: number
    additionalInfo?: Record<string, unknown>
}

// ── State ─────────────────────────────────────────────────────────────────────

const allDevices = ref<TbDevice[]>([])
const treeItems = ref<TreeNode[]>([])
const treeOpened = ref<string[]>([])
const treeActivated = ref<string[]>([])
const deviceSearch = ref('')

const selectedDevice = ref<TbDevice | null>(null)

// ── Computed tree items with search filter ────────────────────────────────────

const filteredTreeItems = computed<TreeNode[]>(() => {
    const q = deviceSearch.value.trim().toLowerCase()
    if (!q) return treeItems.value

    return treeItems.value
        .map(group => ({
            ...group,
            children: group.children?.filter(d =>
                d.title.toLowerCase().includes(q) ||
                (d.label ?? '').toLowerCase().includes(q)
            ),
        }))
        .filter(group => (group.children?.length ?? 0) > 0)
})

// ── Fetch all devices (paginated) ─────────────────────────────────────────────

async function fetchAllDevices(token: string): Promise<TbDevice[]> {
    const PAGE_SIZE = 100
    let page = 0
    const result: TbDevice[] = []

    while (true) {
        const url = `${TB_BASE_URL}/api/tenant/devices?pageSize=${PAGE_SIZE}&page=${page}`
        const response = await axios.get<TbPageData<TbDevice>>(url, { headers: tbHeaders(token) })
        result.push(...response.data.data)
        if (!response.data.hasNext) break
        page++
    }

    return result
}

// ── Build tree grouped by device type ────────────────────────────────────────

function buildTree(devices: TbDevice[]): TreeNode[] {
    const groups = new Map<string, TbDevice[]>()

    for (const device of devices) {
        const type = device.type || 'Sans type'
        if (!groups.has(type)) groups.set(type, [])
        groups.get(type)!.push(device)
    }

    const tree: TreeNode[] = []
    for (const [type, devs] of [...groups.entries()].sort(([a], [b]) => a.localeCompare(b))) {
        tree.push({
            id: `type:${type}`,
            title: `${type} (${devs.length})`,
            isType: true,
            children: devs
                .sort((a, b) => (a.label || a.name).localeCompare(b.label || b.name))
                .map(d => ({
                    id: `device:${d.id.id}`,
                    title: d.label || d.name,
                    label: d.label ? d.name : undefined,
                    deviceId: d.id.id,
                    type: d.type,
                    createdTime: d.createdTime,
                    additionalInfo: d.additionalInfo,
                })),
        })
    }
    return tree
}

// ── Selection ─────────────────────────────────────────────────────────────────

function onActivate(activated: string[]) {
    treeActivated.value = activated
    const nodeId = activated[0]
    if (!nodeId?.startsWith('device:')) {
        selectedDevice.value = null
        return
    }
    const deviceId = nodeId.replace('device:', '')
    selectedDevice.value = allDevices.value.find(d => d.id.id === deviceId) ?? null
}

// ── Load data ─────────────────────────────────────────────────────────────────

async function loadDevices() {
    loginError.value = ''
    isLoading.value = true
    selectedDevice.value = null
    treeActivated.value = []
    try {
        const token = await getTbToken()
        if (!token) return
        const devices = await fetchAllDevices(token)
        allDevices.value = devices
        treeItems.value = buildTree(devices)
        treeOpened.value = treeItems.value.map(g => g.id)
    } catch (e) {
        notifyError(e)
    } finally {
        isLoading.value = false
    }
}

onMounted(loadDevices)

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(ms: number): string {
    return new Date(ms).toLocaleDateString('fr-CA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    })
}
</script>

<template>
    <v-card class="mx-4 my-4 page-card" rounded="xl" :loading="isLoading"
        style="height: 85vh; display: flex; flex-direction: column;">

        <!-- Page header -->
        <div class="page-header px-5 pt-5 pb-3">
            <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
                <div class="d-flex align-center" style="gap: 12px;">
                    <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
                        <v-icon icon="M3 6H21V4H3C1.9 4 1 4.9 1 6V18C1 19.1 1.9 20 3 20H7V18H3V6M13 12H9V13.78C8.39 14.33 8 15.11 8 16C8 16.89 8.39 17.67 9 18.22V20H13V18.22C13.61 17.67 14 16.88 14 16S13.61 14.33 13 13.78V12M11 17.5C10.17 17.5 9.5 16.83 9.5 16S10.17 14.5 11 14.5 12.5 15.17 12.5 16 11.83 17.5 11 17.5M22 8H16C15.5 8 15 8.5 15 9V19C15 19.5 15.5 20 16 20H22C22.5 20 23 19.5 23 19V9C23 8.5 22.5 8 22 8M21 18H17V10H21V18Z" size="26" />
                    </v-avatar>
                    <div>
                        <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Appareils IoT</div>
                        <div class="text-caption text-medium-emphasis">
                            {{ allDevices.length }} appareil{{ allDevices.length !== 1 ? 's' : '' }} ThingsBoard
                        </div>
                    </div>
                </div>
                <div class="d-flex align-center" style="gap: 8px;">
                    <v-btn color="primary" variant="tonal" rounded="pill" prepend-icon="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"
                        :loading="isLoading" @click="loadDevices">
                        Rafraîchir
                    </v-btn>
                </div>
            </div>
        </div>

        <v-divider />

        <!-- Login error banner -->
        <v-alert v-if="loginError" type="error" variant="tonal" density="compact" class="mx-4 mt-3 mb-0"
            rounded="lg">
            {{ loginError }}
        </v-alert>

        <!-- Main content: tree + detail panel -->
        <div class="d-flex" style="flex: 1 1 auto; overflow: hidden;">

            <!-- Left: device tree -->
            <div class="tree-panel d-flex flex-column"
                :style="{ width: $vuetify.display.smAndDown ? '100%' : '300px', borderRight: '1px solid rgba(var(--v-border-color), var(--v-border-opacity))' }">

                <!-- Search -->
                <div class="pa-3">
                    <v-text-field v-model="deviceSearch" label="Rechercher un appareil" density="compact"
                        variant="outlined" rounded="lg" hide-details clearable prepend-inner-icon="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z" />
                </div>

                <v-divider />

                <!-- Tree view -->
                <div style="flex: 1 1 auto; overflow-y: auto;">
                    <v-list density="compact" nav open-strategy="multiple" :opened="treeOpened"
                        @update:opened="treeOpened = $event">
                        <v-list-group v-for="group in filteredTreeItems" :key="group.id" :value="group.id">
                            <template #activator="{ props }">
                                <v-list-item v-bind="props" density="compact" rounded="lg">
                                    <template #prepend>
                                        <v-icon icon="M20,18H4V8H20M20,6H12L10,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V8C22,6.89 21.1,6 20,6Z" size="18" color="primary" class="mr-2" />
                                    </template>
                                    <v-list-item-title class="text-caption font-weight-medium">
                                        {{ group.title }}
                                    </v-list-item-title>
                                </v-list-item>
                            </template>

                            <v-list-item
                                v-for="device in group.children"
                                :key="device.id"
                                density="compact"
                                rounded="lg"
                                :active="treeActivated[0] === device.id"
                                color="primary"
                                @click="onActivate([device.id])"
                            >
                                <template #prepend>
                                    <v-icon icon="M6,4H18V5H21V7H18V9H21V11H18V13H21V15H18V17H21V19H18V20H6V19H3V17H6V15H3V13H6V11H3V9H6V7H3V5H6V4M11,15V18H12V15H11M13,15V18H14V15H13M15,15V18H16V15H15Z" size="16" color="secondary" class="mr-2" />
                                </template>
                                <v-list-item-title class="text-caption">{{ device.title }}</v-list-item-title>
                                <v-list-item-subtitle v-if="device.label" class="text-caption">
                                    {{ device.label }}
                                </v-list-item-subtitle>
                            </v-list-item>
                        </v-list-group>
                    </v-list>

                    <div v-if="!isLoading && filteredTreeItems.length === 0 && !loginError"
                        class="pa-4 text-center text-medium-emphasis text-caption">
                        Aucun appareil trouvé
                    </div>
                </div>
            </div>

            <!-- Right: device detail — hidden on mobile when tree is shown -->
            <div v-if="!$vuetify.display.smAndDown || selectedDevice"
                style="flex: 1 1 auto; overflow-y: auto; padding: 16px;">

                <!-- Placeholder when nothing selected -->
                <div v-if="!selectedDevice"
                    class="d-flex flex-column align-center justify-center text-medium-emphasis"
                    style="height: 100%; gap: 12px;">
                    <v-icon icon="M10,9A1,1 0 0,1 11,8A1,1 0 0,1 12,9V13.47L13.21,13.6L18.15,15.79C18.68,16.03 19,16.56 19,17.14V21.5C18.97,22.32 18.32,22.97 17.5,23H11C10.62,23 10.26,22.85 10,22.57L5.1,18.37L5.84,17.6C6.03,17.39 6.3,17.28 6.58,17.28H6.8L10,19V9M11,5A4,4 0 0,1 15,9C15,10.5 14.2,11.77 13,12.46V11.24C13.61,10.69 14,9.89 14,9A3,3 0 0,0 11,6A3,3 0 0,0 8,9C8,9.89 8.39,10.69 9,11.24V12.46C7.8,11.77 7,10.5 7,9A4,4 0 0,1 11,5Z" size="48" />
                    <span class="text-body-2">Sélectionnez un appareil dans l'arbre</span>
                </div>

                <!-- Device detail card -->
                <template v-else>
                    <!-- Back button on mobile -->
                    <v-btn v-if="$vuetify.display.smAndDown" variant="text" prepend-icon="M20,11V13H8L13.5,18.5L12.08,19.92L4.16,12L12.08,4.08L13.5,5.5L8,11H20Z"
                        class="mb-3" @click="selectedDevice = null; treeActivated = []">
                        Retour
                    </v-btn>

                    <v-card variant="outlined" rounded="lg">
                        <v-card-title class="d-flex align-center" style="gap: 10px;">
                            <v-avatar color="secondary" variant="tonal" size="36" rounded="lg">
                                <v-icon icon="M6,4H18V5H21V7H18V9H21V11H18V13H21V15H18V17H21V19H18V20H6V19H3V17H6V15H3V13H6V11H3V9H6V7H3V5H6V4M11,15V18H12V15H11M13,15V18H14V15H13M15,15V18H16V15H15Z" size="20" />
                            </v-avatar>
                            <div>
                                <div class="text-subtitle-1 font-weight-bold">
                                    {{ selectedDevice.label || selectedDevice.name }}
                                </div>
                                <div v-if="selectedDevice.label" class="text-caption text-medium-emphasis">
                                    {{ selectedDevice.name }}
                                </div>
                            </div>
                        </v-card-title>

                        <v-divider />

                        <v-card-text>
                            <v-list density="compact" lines="two">
                                <v-list-item prepend-icon="M21.41 11.58L12.41 2.58A2 2 0 0 0 11 2H4A2 2 0 0 0 2 4V11A2 2 0 0 0 2.59 12.42L11.59 21.42A2 2 0 0 0 13 22A2 2 0 0 0 14.41 21.41L21.41 14.41A2 2 0 0 0 22 13A2 2 0 0 0 21.41 11.58M13 20L4 11V4H11L20 13M6.5 5A1.5 1.5 0 1 1 5 6.5A1.5 1.5 0 0 1 6.5 5Z" title="Type">
                                    <template #subtitle>
                                        <v-chip size="x-small" color="primary" variant="tonal" class="mt-1">
                                            {{ selectedDevice.type || '—' }}
                                        </v-chip>
                                    </template>
                                </v-list-item>

                                <v-list-item prepend-icon="M10 7V9H9V15H10V17H6V15H7V9H6V7H10M16 7C17.11 7 18 7.9 18 9V15C18 16.11 17.11 17 16 17H12V7M16 9H14V15H16V9Z" title="Identifiant"
                                    :subtitle="selectedDevice.id.id" />

                                <v-list-item prepend-icon="M12 12H17V17H12V12M19 3H18V1H16V3H8V1H6V3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3M19 5V7H5V5H19M5 19V9H19V19H5Z" title="Date de création"
                                    :subtitle="formatDate(selectedDevice.createdTime)" />
                            </v-list>

                            <!-- Additional info -->
                            <template
                                v-if="selectedDevice.additionalInfo && Object.keys(selectedDevice.additionalInfo).length > 0">
                                <v-divider class="my-3" />
                                <div class="text-caption text-medium-emphasis mb-2">Informations additionnelles</div>
                                <v-card variant="tonal" rounded="lg" class="pa-3">
                                    <pre class="text-caption"
                                        style="white-space: pre-wrap; word-break: break-all;">{{ JSON.stringify(selectedDevice.additionalInfo, null, 2) }}</pre>
                                </v-card>
                            </template>
                        </v-card-text>
                    </v-card>
                </template>
            </div>
        </div>
    </v-card>
</template>

<style scoped>
/* ── Page header ─────────────────────────── */
.page-header {
    background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}

/* ── Tree panel ──────────────────────────── */
.tree-panel {
    flex-shrink: 0;
}
</style>
